import React, {
  memo,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import dayjs from "dayjs";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faTimes,
  faPlaneDeparture,
  faPlaneArrival,
} from "@fortawesome/free-solid-svg-icons";
import {
  parseDDMMYYYYToLocal,
  formatDateIL,
  toLocalDateKey,
  diffNights,
  formatDayNameIL,
} from "../../../utils/DatesHelpers";
import { getReturnDatesForDeparture } from "../../../contexts/SearchContextHelpers";
import DayCell from "./DayCell";
import {
  toNormalizedYmd,
  normalizeDateListToYmdSet,
  isDateInList,
  formatMonthKey,
  startOfTodayKey,
  buildMonthsToShow,
  buildMonthDayModels,
  buildMonthTileModels,
  syncHoverPreviewClasses,
} from "./dateRangePickerHelpers";
import "./DateRangePicker.scss";

const MONTHS_AHEAD = 12;

function EndpointPlaneIcon({ isDeparture, className = "" }) {
  return (
    <FontAwesomeIcon
      icon={isDeparture ? faPlaneArrival : faPlaneDeparture}
      className={classNames("drp-endpoint-plane-icon", className)}
      aria-hidden
    />
  );
}

const MonthTiles = memo(function MonthTiles({
  tiles,
  selectedMonth,
  languageCode,
  onSelect,
}) {
  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(languageCode, { month: "long" }),
    [languageCode]
  );

  if (!tiles.length) {
    return (
      <p className="drp-month-tiles-empty" role="status">
        אין חודשים זמינים
      </p>
    );
  }

  return (
    <div className="drp-month-tiles" dir="rtl">
      {tiles.map((tile) => {
        const selected = selectedMonth === tile.key;
        return (
          <button
            type="button"
            key={tile.key}
            className={classNames("drp-month-tile", {
              "drp-month-tile--selected": selected,
            })}
            aria-pressed={selected}
            onClick={() => onSelect(tile.key)}
          >
            <span className="drp-month-tile__name">
              {monthFormatter.format(new Date(tile.year, tile.monthIndex, 1))}
            </span>
            <span className="drp-month-tile__year">{tile.year}</span>
          </button>
        );
      })}
    </div>
  );
});

const MonthPanel = memo(function MonthPanel({
  year,
  month,
  monthTitle,
  isSelectedMonth,
  monthSelectable,
  isMonthMode,
  dayNames,
  startDay,
  days,
  isAgentAppearance,
  priceLinePrefix,
  onSelectDay,
  onMonthTitleSelect,
}) {
  const emptyCells = [];
  for (let i = 0; i < startDay; i++) {
    emptyCells.push(
      <div key={`empty-${year}-${month}-${i}`} className="drp-day drp-day-empty" />
    );
  }

  const monthHeading = (
    <>
      <span className="drp-month-title__label">
        {monthTitle} {year}
      </span>
      {isSelectedMonth ? (
        <span className="drp-month-title__badge">נבחר</span>
      ) : null}
    </>
  );

  return (
    <div
      className={classNames("drp-month", {
        "drp-month--selected": isSelectedMonth,
        "drp-month--selectable": monthSelectable,
      })}
    >
      {isMonthMode ? (
        <button
          type="button"
          className={classNames("drp-month-title", "drp-month-title--button", {
            "drp-month-title--selected": isSelectedMonth,
            "drp-month-title--disabled": !monthSelectable,
          })}
          onClick={() => onMonthTitleSelect(year, month)}
          disabled={!monthSelectable}
        >
          {monthHeading}
        </button>
      ) : (
        <div className="drp-month-title">{monthHeading}</div>
      )}
      <div className="drp-weekdays">
        {dayNames.map((name, i) => (
          <div key={i} className="drp-weekday">
            {name}
          </div>
        ))}
      </div>
      <div className="drp-days">
        {emptyCells}
        {days.map((day) => (
          <DayCell
            key={day.cellKey}
            cellKey={day.cellKey}
            dayNum={day.dayNum}
            dateStr={day.dateStr}
            disabled={day.disabled}
            isArrival={day.isArrival}
            isDeparture={day.isDeparture}
            isBetween={day.isBetween}
            isSelectedMonthDay={isSelectedMonth && !day.disabled}
            isMonthMode={isMonthMode}
            isAgentAppearance={isAgentAppearance}
            hasPrice={day.hasPrice}
            priceRounded={day.pRounded}
            toneCls={day.toneCls}
            priceLinePrefix={priceLinePrefix}
            onSelect={onSelectDay}
          />
        ))}
      </div>
    </div>
  );
});

const DateRangePicker = ({
  calendarDateArrival,
  calendarDateLeave,
  saveArrivalDate,
  saveDepartureDate,
  possibleArrivalDates,
  possibleDepartureDates,
  pricesByDate = null,
  onClose,
  variant = "desktop",
  ldsDates = null,
  tripType = "RoundTrip",
  unrestrictedReturnDates = false,
  closeOnRoundTripComplete = true,
  embedVariant = null,
  hideHeaderDateSummary = false,
  showPhoneCloseButton = true,
  confirmButtonLabel = null,
  priceLinePrefix = null,
  priceToneBand = false,
  destinationCode = "",
  appearance = "default",
  enableMonthMode = false,
  dateMode = "exact",
  onDateModeChange = null,
  selectedMonth = "",
  onMonthSelect = null,
  availableMonths = null,
}) => {
  const isAgentAppearance = appearance === "agent";
  const isMonthMode = enableMonthMode && dateMode === "month";
  const isOneWay = tripType === "OneWay";
  const isPhone = variant === "phone";
  const languageCode =
    typeof localStorage !== "undefined" && localStorage.getItem("i18nextLng")
      ? localStorage.getItem("i18nextLng")
      : "he";

  const [draftArrival, setDraftArrival] = useState("");
  const [draftLeave, setDraftLeave] = useState("");
  const [monthOffset, setMonthOffset] = useState(0);
  const [monthIndex, setMonthIndex] = useState(0);
  const [swipeDir, setSwipeDir] = useState("none");
  const swipeStartRef = useRef({ x: 0, y: 0 });
  const isVerticalScrollRef = useRef(false);
  const activePointerIdRef = useRef(null);
  const monthsRegionRef = useRef(null);
  const hoverKeyRef = useRef(null);
  const previewMetaRef = useRef({ arrivalKey: "", pickingReturn: false });

  const arrival = isPhone ? draftArrival : (calendarDateArrival ?? "");
  const departure = isPhone ? draftLeave : (calendarDateLeave ?? "");

  useEffect(() => {
    if (isPhone) {
      setDraftArrival(calendarDateArrival ?? "");
      setDraftLeave(calendarDateLeave ?? "");
    }
  }, [isPhone, calendarDateArrival, calendarDateLeave]);

  const arrivalDateObj = useMemo(
    () => (arrival ? parseDDMMYYYYToLocal(arrival) : null),
    [arrival]
  );
  const departureDateObj = useMemo(
    () => (departure ? parseDDMMYYYYToLocal(departure) : null),
    [departure]
  );
  const arrivalKey = arrivalDateObj ? toLocalDateKey(arrivalDateObj) : "";
  const departureKey = departureDateObj ? toLocalDateKey(departureDateObj) : "";

  const isSettingArrival = isOneWay || !arrival || (!!arrival && !!departure);
  const pickingReturn =
    !isMonthMode && !isSettingArrival && !!arrivalKey && !departureKey;

  previewMetaRef.current = { arrivalKey, pickingReturn };

  const priceBand = useMemo(() => {
    if (!priceToneBand || !pricesByDate) return null;
    const vals = Object.values(pricesByDate).filter(
      (x) => typeof x === "number" && x > 0
    );
    if (vals.length < 2) return null;
    const pMin = Math.min(...vals);
    const pMax = Math.max(...vals);
    if (pMax <= pMin) return null;
    return { pMin, pMax, span: pMax - pMin };
  }, [priceToneBand, pricesByDate]);

  const effectivePossibleDepartureDates = useMemo(() => {
    if (isPhone && ldsDates && arrival) {
      return getReturnDatesForDeparture(ldsDates, arrival);
    }
    return possibleDepartureDates;
  }, [isPhone, ldsDates, arrival, possibleDepartureDates]);

  const normalizedArrivalSet = useMemo(
    () => normalizeDateListToYmdSet(possibleArrivalDates),
    [possibleArrivalDates]
  );
  const normalizedDepartureSet = useMemo(
    () => normalizeDateListToYmdSet(effectivePossibleDepartureDates),
    [effectivePossibleDepartureDates]
  );

  const monthAvailabilitySet = useMemo(() => {
    if (!Array.isArray(availableMonths) || availableMonths.length === 0) {
      return null;
    }
    return new Set(
      availableMonths.map((m) => String(m || "").trim()).filter(Boolean)
    );
  }, [availableMonths]);

  const isMonthAvailable = useCallback(
    (monthKey) => {
      if (!monthKey) return false;
      if (!monthAvailabilitySet) return true;
      return monthAvailabilitySet.has(monthKey);
    },
    [monthAvailabilitySet]
  );

  const formatMonthLabel = useCallback(
    (monthKey) => {
      if (!monthKey) return "";
      const parsed = dayjs(monthKey, "MM/YYYY");
      if (!parsed.isValid()) return monthKey;
      return new Intl.DateTimeFormat(languageCode, {
        month: "long",
        year: "numeric",
      }).format(parsed.toDate());
    },
    [languageCode]
  );

  const handleMonthTitleSelect = useCallback(
    (year, monthIdx) => {
      if (!isMonthMode || !onMonthSelect) return;
      const monthKey = formatMonthKey(year, monthIdx);
      if (!isMonthAvailable(monthKey)) return;
      onMonthSelect(monthKey);
      if (!isPhone && onClose) onClose();
    },
    [isMonthMode, onMonthSelect, isMonthAvailable, isPhone, onClose]
  );

  const handleMonthTileSelect = useCallback(
    (monthKey) => {
      if (!isMonthMode || !onMonthSelect) return;
      if (!isMonthAvailable(monthKey)) return;
      onMonthSelect(monthKey);
      if (!isPhone && onClose) onClose();
    },
    [isMonthMode, onMonthSelect, isMonthAvailable, isPhone, onClose]
  );

  const monthTiles = useMemo(
    () =>
      buildMonthTileModels({
        availableMonths,
        monthsAhead: MONTHS_AHEAD,
      }),
    [availableMonths]
  );

  useEffect(() => {
    if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
    const normalizedArrivalDates = Array.from(normalizedArrivalSet || []).sort();
    const firstAvailableDate = normalizedArrivalDates[0] || null;
    const lastAvailableDate =
      normalizedArrivalDates[normalizedArrivalDates.length - 1] || null;
    let disabledReason = null;
    if (normalizedArrivalSet && normalizedArrivalSet.size === 0) {
      disabledReason = "no_available_dates";
    } else if (arrival && !isDateInList(arrival, normalizedArrivalSet)) {
      disabledReason = "selected_outbound_not_available";
    } else if (
      !isOneWay &&
      !unrestrictedReturnDates &&
      departure &&
      !isDateInList(departure, normalizedDepartureSet)
    ) {
      disabledReason = "selected_return_not_available";
    }
    console.log("[FLIGHT_DATE_PICKER_AVAILABILITY_AUDIT]", {
      destinationCode: String(destinationCode || "").trim() || null,
      availableDatesCount: normalizedArrivalDates.length,
      firstAvailableDate,
      lastAvailableDate,
      selectedOutboundDate: arrival ? toNormalizedYmd(arrival) : null,
      selectedReturnDate: departure ? toNormalizedYmd(departure) : null,
      disabledReason,
    });
  }, [
    normalizedArrivalSet,
    normalizedDepartureSet,
    destinationCode,
    arrival,
    departure,
    isOneWay,
    unrestrictedReturnDates,
  ]);

  const clearHoverPreview = useCallback(() => {
    hoverKeyRef.current = null;
    syncHoverPreviewClasses(monthsRegionRef.current, null, previewMetaRef.current);
  }, []);

  const handleSelectDate = useCallback(
    (formatted) => {
      if (isMonthMode) return;
      if (!formatted) return;
      clearHoverPreview();

      const isSetting =
        isOneWay || !arrival || (!!arrival && !!departure);

      if (isSetting) {
        if (!isDateInList(formatted, normalizedArrivalSet)) return;
        if (isPhone) {
          setDraftArrival(formatted);
          setDraftLeave("");
        } else {
          saveArrivalDate(formatted);
          saveDepartureDate("");
          if (isOneWay && onClose) onClose();
        }
        return;
      }

      const currentArrival = arrival ? parseDDMMYYYYToLocal(arrival) : null;
      const picked = parseDDMMYYYYToLocal(formatted);
      if (!currentArrival || !picked || picked <= currentArrival) return;
      if (
        !unrestrictedReturnDates &&
        !isDateInList(formatted, normalizedDepartureSet)
      )
        return;
      if (isPhone) {
        setDraftLeave(formatted);
      } else {
        saveDepartureDate(formatted);
        if (closeOnRoundTripComplete && onClose) onClose();
      }
    },
    [
      isMonthMode,
      isOneWay,
      arrival,
      departure,
      normalizedArrivalSet,
      normalizedDepartureSet,
      unrestrictedReturnDates,
      isPhone,
      saveArrivalDate,
      saveDepartureDate,
      onClose,
      closeOnRoundTripComplete,
      clearHoverPreview,
    ]
  );

  const handleConfirm = () => {
    if (isMonthMode) {
      if (!selectedMonth) return;
      if (onClose) onClose();
      return;
    }
    if (isOneWay) {
      if (!draftArrival) return;
      saveArrivalDate(draftArrival);
      saveDepartureDate("");
      if (onClose) onClose();
      return;
    }
    if (!draftArrival || !draftLeave) return;
    saveArrivalDate(draftArrival);
    saveDepartureDate(draftLeave);
    if (closeOnRoundTripComplete && onClose) onClose();
  };

  const monthsToShow = useMemo(() => buildMonthsToShow(MONTHS_AHEAD), []);
  const todayKey = useMemo(() => startOfTodayKey(), []);
  const dayNames = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(languageCode, { weekday: "short" });
    return Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(2023, 0, 1 + i))
    );
  }, [languageCode]);
  const monthTitleFormatter = useMemo(
    () => new Intl.DateTimeFormat(languageCode, { month: "long" }),
    [languageCode]
  );

  const phoneWindowMax = Math.max(0, monthsToShow.length - 2);
  const goPrev = () => {
    setMonthIndex((i) => Math.max(0, i - 1));
    if (variant === "phone") setSwipeDir("right");
  };
  const goNext = () => {
    setMonthIndex((i) =>
      Math.min(variant === "phone" ? phoneWindowMax : monthsToShow.length - 1, i + 1)
    );
    if (variant === "phone") setSwipeDir("left");
  };

  useEffect(() => {
    if (swipeDir === "none") return;
    const t = setTimeout(() => setSwipeDir("none"), 200);
    return () => clearTimeout(t);
  }, [swipeDir]);

  const SWIPE_THRESHOLD = 40;

  const handlePointerDown = (e) => {
    if (!isPhone) return;
    activePointerIdRef.current = e.pointerId;
    swipeStartRef.current = { x: e.clientX, y: e.clientY };
    isVerticalScrollRef.current = false;
  };

  const handlePointerMove = (e) => {
    if (!isPhone || activePointerIdRef.current !== e.pointerId) return;
    const { x: startX, y: startY } = swipeStartRef.current;
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      isVerticalScrollRef.current = true;
    }
  };

  const handlePointerUp = (e) => {
    if (!isPhone || activePointerIdRef.current !== e.pointerId) return;
    const { x: startX } = swipeStartRef.current;
    const deltaX = e.clientX - startX;
    if (isVerticalScrollRef.current) return;
    if (Math.abs(deltaX) >= SWIPE_THRESHOLD) {
      if (deltaX > 0) {
        if (monthIndex > 0) goPrev();
      } else if (monthIndex < monthsToShow.length - 1) {
        goNext();
      }
    }
    activePointerIdRef.current = null;
  };

  const handlePointerCancel = () => {
    activePointerIdRef.current = null;
  };

  const handleMonthsHover = (e) => {
    const el = e.target.closest?.("[data-day-key]");
    if (!el || el.classList.contains("drp-day-disabled")) {
      if (hoverKeyRef.current != null) clearHoverPreview();
      return;
    }
    const nextKey = el.getAttribute("data-day-key");
    if (hoverKeyRef.current === nextKey) return;
    hoverKeyRef.current = nextKey;
    syncHoverPreviewClasses(
      monthsRegionRef.current,
      nextKey,
      previewMetaRef.current
    );
  };

  const visibleMonths = useMemo(() => {
    return variant === "phone"
      ? monthsToShow.slice(monthIndex, monthIndex + 2)
      : monthsToShow.slice(
          Math.max(0, monthOffset),
          Math.min(monthsToShow.length, monthOffset + 2)
        );
  }, [variant, monthsToShow, monthIndex, monthOffset]);

  const monthModels = useMemo(
    () => {
      if (isMonthMode) return [];
      return visibleMonths.map(({ year, month }) => {
        const monthKey = formatMonthKey(year, month);
        const isSelectedMonth = isMonthMode && selectedMonth === monthKey;
        const monthSelectable = isMonthMode && isMonthAvailable(monthKey);
        const { startDay, days } = buildMonthDayModels({
          year,
          month,
          todayKey,
          arrivalKey,
          departureKey,
          isSettingArrival,
          unrestrictedReturnDates,
          arrivalSet: normalizedArrivalSet,
          departureSet: normalizedDepartureSet,
          pricesByDate,
          priceToneBand,
          priceBand,
          isMonthMode,
          monthSelectable,
        });
        return {
          year,
          month,
          monthTitle: monthTitleFormatter.format(new Date(year, month, 1)),
          isSelectedMonth,
          monthSelectable,
          startDay,
          days,
        };
      });
    },
    [
      visibleMonths,
      isMonthMode,
      selectedMonth,
      isMonthAvailable,
      todayKey,
      arrivalKey,
      departureKey,
      isSettingArrival,
      unrestrictedReturnDates,
      normalizedArrivalSet,
      normalizedDepartureSet,
      pricesByDate,
      priceToneBand,
      priceBand,
      monthTitleFormatter,
    ]
  );

  const nightsCount = diffNights(arrivalDateObj, departureDateObj);
  const canConfirm = isMonthMode
    ? !!selectedMonth
    : isOneWay
      ? !!draftArrival
      : !!draftArrival && !!draftLeave;
  const isRtl = typeof document !== "undefined" && document.documentElement?.dir === "rtl";

  const displayStartObj =
    arrivalDateObj && departureDateObj && arrivalDateObj > departureDateObj
      ? departureDateObj
      : arrivalDateObj;
  const displayEndObj =
    arrivalDateObj && departureDateObj && arrivalDateObj > departureDateObj
      ? arrivalDateObj
      : departureDateObj;
  const displayStartStr = displayStartObj ? formatDateIL(displayStartObj) : (arrival || "__/__/__");
  const displayEndStr = displayEndObj ? formatDateIL(displayEndObj) : (departure || "__/__/__");
  const displayNightsCount =
    displayStartObj && displayEndObj ? diffNights(displayStartObj, displayEndObj) : nightsCount;

  const renderAgentHeaderChips = () => {
    if (isMonthMode) {
      return (
        <div className="drp-agent-header-chips" dir="rtl">
          <div className="drp-agent-header-chip drp-agent-header-chip--month">
            <span className="drp-agent-header-chip__label">חודש</span>
            <span className="drp-agent-header-chip__value">
              {selectedMonth
                ? formatMonthLabel(selectedMonth)
                : "בחרו חודש"}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="drp-agent-header-chips" dir="rtl">
        <div
          className={classNames(
            "drp-agent-header-chip",
            "drp-agent-header-chip--departure",
            { "drp-agent-header-chip--active": isSettingArrival }
          )}
        >
          <span className="drp-agent-header-chip__label">
            <EndpointPlaneIcon className="drp-agent-header-chip__plane" />
            יציאה
          </span>
          <span className="drp-agent-header-chip__value">
            {displayStartObj
              ? `${displayStartStr} · ${formatDayNameIL(displayStartObj)}`
              : "בחירת תאריך"}
          </span>
        </div>
        {!isOneWay ? (
          <>
            <span className="drp-agent-header-chips__rule" aria-hidden="true" />
            <div
              className={classNames(
                "drp-agent-header-chip",
                "drp-agent-header-chip--return",
                { "drp-agent-header-chip--active": pickingReturn }
              )}
            >
              <span className="drp-agent-header-chip__label">
                <EndpointPlaneIcon isDeparture className="drp-agent-header-chip__plane" />
                חזרה
              </span>
              <span className="drp-agent-header-chip__value">
                {displayEndObj
                  ? `${displayEndStr} · ${formatDayNameIL(displayEndObj)}`
                  : pickingReturn
                    ? "בחירת תאריך"
                    : "—"}
              </span>
            </div>
          </>
        ) : null}
        {displayNightsCount != null && displayNightsCount >= 0 ? (
          <span className="drp-nights">
            {displayNightsCount}{" "}
            {displayNightsCount === 1 ? "לילה" : "לילות"}
          </span>
        ) : null}
      </div>
    );
  };

  const renderPhoneDateChips = () => {
    if (isMonthMode) {
      return (
        <div className="drp-phone-chip drp-phone-chip--month">
          <span className="drp-phone-chip-label">חודש</span>
          <span className="drp-phone-chip-value">
            {selectedMonth ? formatMonthLabel(selectedMonth) : "בחר חודש"}
          </span>
        </div>
      );
    }
    return (
      <>
        <div
          className={classNames("drp-phone-chip", "drp-phone-chip--departure", {
            "drp-phone-chip--active": isSettingArrival,
          })}
        >
          <span className="drp-phone-chip-label">
            <EndpointPlaneIcon className="drp-phone-chip-plane" />
            יציאה
          </span>
          <span className="drp-phone-chip-value">
            {arrival || "בחירת תאריך"}
          </span>
        </div>
        {!isOneWay ? (
          <div
            className={classNames("drp-phone-chip", "drp-phone-chip--return", {
              "drp-phone-chip--active": pickingReturn,
            })}
          >
            <span className="drp-phone-chip-label">
              <EndpointPlaneIcon isDeparture className="drp-phone-chip-plane" />
              חזרה
            </span>
            <span className="drp-phone-chip-value">
              {departure || (pickingReturn ? "בחירת תאריך" : "—")}
            </span>
          </div>
        ) : null}
        {!isOneWay && displayNightsCount != null && displayNightsCount >= 0 ? (
          <span className="drp-nights drp-nights--chip">
            ({displayNightsCount}{" "}
            {displayNightsCount === 1 ? "לילה" : "לילות"})
          </span>
        ) : null}
      </>
    );
  };

  const renderedMonths = monthModels.map((model) => (
    <MonthPanel
      key={`${model.year}-${model.month}`}
      year={model.year}
      month={model.month}
      monthTitle={model.monthTitle}
      isSelectedMonth={model.isSelectedMonth}
      monthSelectable={model.monthSelectable}
      isMonthMode={isMonthMode}
      dayNames={dayNames}
      startDay={model.startDay}
      days={model.days}
      isAgentAppearance={isAgentAppearance}
      priceLinePrefix={priceLinePrefix}
      onSelectDay={handleSelectDate}
      onMonthTitleSelect={handleMonthTitleSelect}
    />
  ));

  return (
    <div
      className={classNames("drp-root", `drp-${variant}`, {
        "drp-chat": embedVariant === "chat",
        "drp-agent": isAgentAppearance,
        "drp-month-mode": isMonthMode,
        "drp-picking-return": pickingReturn,
      })}
    >
      {enableMonthMode ? (
        <div className="drp-mode-toggle" dir="rtl" role="tablist" aria-label="מצב תאריכים">
          <button
            type="button"
            role="tab"
            aria-selected={!isMonthMode}
            className={classNames("drp-mode-toggle__btn", {
              "drp-mode-toggle__btn--active": !isMonthMode,
            })}
            onClick={() => onDateModeChange?.("exact")}
          >
            תאריכים מדויקים
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isMonthMode}
            className={classNames("drp-mode-toggle__btn", {
              "drp-mode-toggle__btn--active": isMonthMode,
            })}
            onClick={() => onDateModeChange?.("month")}
          >
            חודש שלם
          </button>
        </div>
      ) : null}
      <header
        className={classNames("drp-header", {
          "drp-header--no-date-summary": hideHeaderDateSummary && variant !== "phone",
          "drp-header--agent": isAgentAppearance,
        })}
      >
        {variant === "phone" ? (
          <>
            {isAgentAppearance ? (
              <div className="drp-phone-dates-chips" dir="rtl">
                {renderPhoneDateChips()}
              </div>
            ) : null}
            {!isMonthMode ? (
              <div className="drp-phone-nav-row" dir="rtl">
                <button
                  type="button"
                  className={classNames("drp-nav-btn", {
                    "drp-nav-btn--with-label": !isAgentAppearance,
                  })}
                  onClick={goNext}
                  disabled={monthIndex >= phoneWindowMax}
                  aria-label="חודש קדימה"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                  {!isAgentAppearance ? (
                    <span className="drp-nav-btn__hint">חודש קדימה</span>
                  ) : null}
                </button>
                <button
                  type="button"
                  className={classNames("drp-nav-btn", {
                    "drp-nav-btn--with-label": !isAgentAppearance,
                  })}
                  onClick={goPrev}
                  disabled={monthIndex <= 0}
                  aria-label="חודש אחורה"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                  {!isAgentAppearance ? (
                    <span className="drp-nav-btn__hint">חודש אחורה</span>
                  ) : null}
                </button>
              </div>
            ) : null}
            {showPhoneCloseButton && onClose ? (
              <button
                type="button"
                className="drp-close-btn"
                onClick={onClose}
                aria-label="סגור"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            ) : null}
          </>
        ) : (
          <>
            {!hideHeaderDateSummary ? (
              isAgentAppearance ? (
                renderAgentHeaderChips()
              ) : (
                <div className="drp-header-dates">
                  <span className="drp-header-label">
                    {isOneWay ? (
                      <>
                        {arrival || "__/__/__"}{" "}
                        {arrivalDateObj && formatDayNameIL(arrivalDateObj)}
                      </>
                    ) : (
                      <>
                        {displayStartStr}{" "}
                        {displayStartObj && formatDayNameIL(displayStartObj)} –{" "}
                        {displayEndStr}{" "}
                        {displayEndObj && formatDayNameIL(displayEndObj)}
                      </>
                    )}
                  </span>
                  {!isOneWay &&
                    displayNightsCount != null &&
                    displayNightsCount >= 0 && (
                      <span className="drp-nights">
                        ({displayNightsCount}{" "}
                        {displayNightsCount === 1 ? "לילה" : "לילות"})
                      </span>
                    )}
                </div>
              )
            ) : null}
            <div className="drp-header-controls">
              {!isMonthMode ? (
                <div className="drp-header-nav" dir={isRtl ? "rtl" : "ltr"}>
                  <button
                    type="button"
                    className={classNames("drp-nav-btn", {
                      "drp-nav-btn--with-label": !isAgentAppearance,
                    })}
                    onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
                    disabled={monthOffset <= 0}
                    aria-label="חודש אחורה"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                    {!isAgentAppearance ? (
                      <span className="drp-nav-btn__hint">חודש אחורה</span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className={classNames("drp-nav-btn", {
                      "drp-nav-btn--with-label": !isAgentAppearance,
                    })}
                    onClick={() =>
                      setMonthOffset((o) => Math.min(MONTHS_AHEAD - 2, o + 1))
                    }
                    disabled={monthOffset >= MONTHS_AHEAD - 2}
                    aria-label="חודש קדימה"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                    {!isAgentAppearance ? (
                      <span className="drp-nav-btn__hint">חודש קדימה</span>
                    ) : null}
                  </button>
                </div>
              ) : null}
              {onClose ? (
                <button
                  type="button"
                  className="drp-close-btn"
                  onClick={onClose}
                  aria-label="סגור"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              ) : null}
            </div>
          </>
        )}
      </header>

      <div
        className="drp-body"
        onPointerDown={isPhone && !isMonthMode ? handlePointerDown : undefined}
        onPointerMove={isPhone && !isMonthMode ? handlePointerMove : undefined}
        onPointerUp={isPhone && !isMonthMode ? handlePointerUp : undefined}
        onPointerLeave={isPhone && !isMonthMode ? handlePointerUp : undefined}
        onPointerCancel={isPhone && !isMonthMode ? handlePointerCancel : undefined}
        style={isPhone ? { touchAction: "pan-y" } : undefined}
      >
        {isMonthMode ? (
          <MonthTiles
            tiles={monthTiles}
            selectedMonth={selectedMonth}
            languageCode={languageCode}
            onSelect={handleMonthTileSelect}
          />
        ) : (
          <div
            ref={monthsRegionRef}
            className={
              variant === "phone"
                ? classNames("drp-phone-months", {
                    "drp-slide-left": swipeDir === "left",
                    "drp-slide-right": swipeDir === "right",
                  })
                : "drp-months-row"
            }
            onMouseOver={handleMonthsHover}
            onMouseLeave={clearHoverPreview}
          >
            {renderedMonths}
          </div>
        )}
      </div>

      {isPhone && (
        <footer className="drp-footer">
          <button
            type="button"
            className="drp-confirm-btn"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {confirmButtonLabel ||
              (isMonthMode ? "אישור חודש" : "אישור תאריכים")}
          </button>
        </footer>
      )}
    </div>
  );
};

export { DateRangePicker };
export default DateRangePicker;
